import { useEffect, useRef, useState } from "react";
import { CardQuestion, CardActions, PressEnterHint } from "./CardPrimitives";
import { useIntakeMobileChromeRegistration } from "../IntakeMobileChromeContext";
import type { CardDef } from "../types";
import { useLingui } from "@lingui/react";
import { msg } from "@lingui/macro";

interface NameCardProps {
  card: CardDef;
  firstNameValue: string;
  lastNameValue: string;
  onFirstNameValue: (v: string) => void;
  onLastNameValue: (v: string) => void;
  onNext: () => void;
  onBack: () => void;
  index: number;
  firstNameError?: string;
  lastNameError?: string;
  loading?: boolean;
}

export function NameCard({
  card,
  firstNameValue,
  lastNameValue,
  onFirstNameValue,
  onLastNameValue,
  onNext,
  onBack,
  index,
  firstNameError,
  lastNameError,
  loading = false,
}: NameCardProps) {
  const { i18n, _ } = useLingui();
  const [touched, setTouched] = useState(false);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstNameRef.current?.focus({ preventScroll: true });
  }, [card.id]);

  const handleKeyFirstName = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      lastNameRef.current?.focus();
    }
  };

  const handleKeyLastName = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setTouched(true);
      onNext();
    }
  };

  const showFirstNameError = touched && firstNameError;
  const showLastNameError = touched && lastNameError;

  useIntakeMobileChromeRegistration(
    () => ({
      onContinue: () => {
        setTouched(true);
        onNext();
      },
      onCancel: onBack,
      continueLoading: loading,
    }),
    [onNext, onBack, loading],
  );

  return (
    <div>
      <CardQuestion question={card.question} hint={card.hint} index={index} />

      <div className="flex flex-col gap-6 mt-6 max-w-[500px]">
        <div className="relative flex-1">
          <label className="block text-[13px] font-medium text-gray-500 mb-1.5 ml-1 uppercase tracking-wider">
            {_(msg`First name`)}
          </label>
          <input
            ref={firstNameRef}
            type="text"
            value={firstNameValue}
            onChange={(e) => {
              onFirstNameValue(e.target.value);
              if (touched) setTouched(false);
            }}
            onKeyDown={handleKeyFirstName}
            placeholder={i18n._(msg`First name`)}
            disabled={loading}
            autoComplete="given-name"
            className={`w-full bg-white/80 backdrop-blur-sm border-[1px] rounded-xl px-5 py-3.5 text-[17px] text-gray-900 placeholder-gray-400 border-gray-300 transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.03)] focus:outline-none focus:ring-0 focus:border-[#166B93] focus:shadow-[0_4px_20px_rgba(22,107,147,0.08)] disabled:opacity-60 disabled:cursor-not-allowed ${
              showFirstNameError
                ? "!border-red-400 focus:!border-red-500"
                : "hover:border-gray-400"
            }`}
          />
          {showFirstNameError && (
            <div className="mt-2 flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-sm font-medium text-red-500">{firstNameError}</p>
            </div>
          )}
        </div>

        <div className="relative flex-1">
          <label className="block text-[13px] font-medium text-gray-500 mb-1.5 ml-1 uppercase tracking-wider">
            {_(msg`Last name`)}
          </label>
          <input
            ref={lastNameRef}
            type="text"
            value={lastNameValue}
            onChange={(e) => {
              onLastNameValue(e.target.value);
              if (touched) setTouched(false);
            }}
            onKeyDown={handleKeyLastName}
            placeholder={i18n._(msg`Last name`)}
            disabled={loading}
            autoComplete="family-name"
            className={`w-full bg-white/80 backdrop-blur-sm border-[1px] rounded-xl px-5 py-3.5 text-[17px] text-gray-900 placeholder-gray-400 border-gray-300 transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.03)] focus:outline-none focus:ring-0 focus:border-[#166B93] focus:shadow-[0_4px_20px_rgba(22,107,147,0.08)] disabled:opacity-60 disabled:cursor-not-allowed ${
              showLastNameError
                ? "!border-red-400 focus:!border-red-500"
                : "hover:border-gray-400"
            }`}
          />
          {showLastNameError && (
            <div className="mt-2 flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-sm font-medium text-red-500">{lastNameError}</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 sm:mt-10">
        <CardActions
          onNext={() => { setTouched(true); onNext(); }}
          onBack={onBack}
          nextLoading={loading}
        />
        {!loading && <PressEnterHint />}
      </div>
    </div>
  );
}
